import { useState } from "react";
import type { ChangeEvent, FormEvent } from "react";
import { contact, site } from "../lib/content";
import { ArrowRightIcon } from "./Icons";

type Status = "idle" | "submitting" | "sent" | "error" | "unconfigured";

/**
 * Where the contact form POSTs. Set VITE_CONTACT_ENDPOINT in .env to a form
 * service (Formspree, Basin, ...) or your own API endpoint.
 *
 * Until that is set the form deliberately does NOT pretend to succeed - it tells
 * the visitor nothing was sent and offers email/WhatsApp instead. Showing a fake
 * "message sent" would silently lose real enquiries.
 */
const endpoint = (import.meta.env.VITE_CONTACT_ENDPOINT as string | undefined)?.trim() ?? "";

const emptyForm = { name: "", email: "", company: "", message: "", company_website: "" };
type FormValues = typeof emptyForm;

const mailtoHref = (() => {
  const subject = encodeURIComponent("Qost.ai enquiry");
  const body = encodeURIComponent(
    "Tell us a little about your AWS setup and what you'd like to see.\n"
  );
  return `mailto:${site.email}?subject=${subject}&body=${body}`;
})();

export default function Contact({ withHeader = true }: { withHeader?: boolean }) {
  const [values, setValues] = useState<FormValues>(emptyForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormValues, string>>>({});
  const [status, setStatus] = useState<Status>("idle");
  const [failure, setFailure] = useState("");

  const field =
    (key: keyof FormValues) =>
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setValues((prev) => ({ ...prev, [key]: event.target.value }));
      setErrors((prev) => ({ ...prev, [key]: undefined }));
      if (status !== "idle") setStatus("idle");
    };

  const validate = () => {
    const next: Partial<Record<keyof FormValues, string>> = {};
    if (!values.name.trim()) next.name = "Please enter your name.";
    if (!values.email.trim()) {
      next.email = "Please enter your email.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(values.email.trim())) {
      next.email = "That doesn't look like a valid email address.";
    }
    if (!values.message.trim()) {
      next.message = "Please tell us what you'd like to solve.";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!validate()) return;

    if (!endpoint) {
      setStatus("unconfigured");
      return;
    }

    setStatus("submitting");
    setFailure("");
    try {
      const response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify(values),
      });
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
      setValues(emptyForm);
      setStatus("sent");
    } catch (error) {
      setFailure(error instanceof Error ? error.message : "Something went wrong.");
      setStatus("error");
    }
  };

  const inputClass = (hasError: boolean) =>
    `mt-2 w-full rounded-xl border bg-white px-4 py-3 text-sm text-ink outline-none transition-colors placeholder:text-ink-muted/60 ${
      hasError
        ? "border-accent-danger focus:border-accent-danger"
        : "border-ink-line focus:border-brand-400 focus:ring-2 focus:ring-brand-100"
    }`;

  const details = [
    { label: "Email us", value: site.email, note: contact.emailNote, href: mailtoHref },
    { label: "Headquarters", value: site.headquarters, note: contact.hqNote },
    { label: "Business hours", value: site.businessHours, note: contact.hoursNote },
  ];

  return (
    <section id="contact" className="section-pad bg-white">
      <div className="container-page">
        <div className="grid gap-12 lg:grid-cols-2">
          <div>
            {withHeader && (
              <>
                <span className="eyebrow">{contact.eyebrow}</span>
                <h2 className="h-section mt-4">{contact.heading}</h2>
                <p className="lead">{contact.subheading}</p>
              </>
            )}

            <dl className={withHeader ? "mt-10 space-y-6" : "space-y-6"}>
              {details.map((item) => (
                <div key={item.label} className="border-l-2 border-brand-200 pl-5">
                  <dt className="text-[11px] font-semibold uppercase tracking-[0.16em] text-ink-muted">
                    {item.label}
                  </dt>
                  <dd className="mt-1">
                    {item.href ? (
                      <a
                        href={item.href}
                        className="text-base font-semibold text-brand-600 hover:text-brand-700"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <span className="text-base font-semibold text-ink">{item.value}</span>
                    )}
                    <span className="mt-0.5 block text-xs text-ink-muted">{item.note}</span>
                  </dd>
                </div>
              ))}
            </dl>

            <p className="mt-8 text-sm text-ink-muted">
              Prefer WhatsApp?{" "}
              <a
                href={site.whatsappUrl}
                target="_blank"
                rel="noreferrer noopener"
                className="font-semibold text-brand-600 hover:text-brand-700"
              >
                {site.whatsapp}
              </a>
            </p>
          </div>

          <form onSubmit={onSubmit} noValidate className="card p-7 shadow-card">
            <h3 className="text-lg font-semibold text-ink">{contact.formHeading}</h3>
            <p className="mt-1 text-sm text-ink-muted">{contact.formSubheading}</p>

            <div className="mt-6 grid gap-5">
              <div>
                <label htmlFor="contact-name" className="text-sm font-semibold text-ink">
                  {contact.fields.name}
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  value={values.name}
                  onChange={field("name")}
                  aria-invalid={Boolean(errors.name)}
                  className={inputClass(Boolean(errors.name))}
                  placeholder="Jane Cooper"
                />
                {errors.name && (
                  <p className="mt-2 text-xs font-medium text-accent-danger">{errors.name}</p>
                )}
              </div>

              <div>
                <label htmlFor="contact-email" className="text-sm font-semibold text-ink">
                  {contact.fields.email}
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={values.email}
                  onChange={field("email")}
                  aria-invalid={Boolean(errors.email)}
                  className={inputClass(Boolean(errors.email))}
                  placeholder="jane@company.com"
                />
                {errors.email && (
                  <p className="mt-2 text-xs font-medium text-accent-danger">{errors.email}</p>
                )}
              </div>

              <div>
                <label htmlFor="contact-company" className="text-sm font-semibold text-ink">
                  {contact.fields.company}{" "}
                  <span className="font-normal text-ink-muted">(optional)</span>
                </label>
                <input
                  id="contact-company"
                  name="company"
                  type="text"
                  autoComplete="organization"
                  value={values.company}
                  onChange={field("company")}
                  className={inputClass(false)}
                  placeholder="Company Inc."
                />
              </div>

              <div>
                <label htmlFor="contact-message" className="text-sm font-semibold text-ink">
                  {contact.fields.message}
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={4}
                  value={values.message}
                  onChange={field("message")}
                  aria-invalid={Boolean(errors.message)}
                  className={`${inputClass(Boolean(errors.message))} resize-y`}
                  placeholder="We run several AWS accounts and need clearer month-end reporting."
                />
                {errors.message && (
                  <p className="mt-2 text-xs font-medium text-accent-danger">{errors.message}</p>
                )}
              </div>

              {/* Honeypot: hidden from humans, bots fill every field they find.
                  The API silently discards any submission that includes it. */}
              <div
                aria-hidden="true"
                className="absolute left-[-9999px] h-0 w-0 overflow-hidden"
              >
                <label htmlFor="contact-company-website">Company website</label>
                <input
                  id="contact-company-website"
                  name="company_website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={values.company_website}
                  onChange={field("company_website")}
                />
              </div>

              <button
                type="submit"
                className="btn-primary w-full"
                disabled={status === "submitting"}
              >
                {status === "submitting" ? "Sending..." : contact.submitLabel}
                {status !== "submitting" && <ArrowRightIcon />}
              </button>

              {status === "sent" && (
                <p role="status" className="rounded-xl bg-accent-success/10 p-4 text-sm text-ink">
                  Thanks — we've got your message and will reply within 24 hours.
                </p>
              )}

              {status === "unconfigured" && (
                <p role="status" className="rounded-xl bg-accent-warning/10 p-4 text-sm text-ink">
                  This form isn't connected to a backend yet, so nothing was sent. Set{" "}
                  <code className="rounded bg-white px-1">VITE_CONTACT_ENDPOINT</code> to enable
                  it, or email{" "}
                  <a href={mailtoHref} className="font-semibold text-brand-600">
                    {site.email}
                  </a>
                  .
                </p>
              )}

              {status === "error" && (
                <p role="alert" className="rounded-xl bg-accent-danger/10 p-4 text-sm text-ink">
                  We couldn't send that ({failure}). Please try again, or email{" "}
                  <a href={mailtoHref} className="font-semibold text-brand-600">
                    {site.email}
                  </a>
                  .
                </p>
              )}
            </div>
          </form>
        </div>
      </div>
    </section>
  );
}
